// Funnel instrumentation for the practice -> test step.
//
// Background: the reveal-arm anomaly is a loss at one button press ("Start the test").
// A trace is only written when a puzzle COMPLETES, so today the data cannot distinguish
//   (a) the participant chose not to start, from
//   (b) they pressed the button and the app failed to start the test.
// These calls make the press itself, and any failure of it, observable.
//
// Every function here is fire-and-forget and MUST NOT throw or block: they run on the click
// path of the thing being measured, and an instrument that can break the funnel it measures
// is worse than no instrument. Both sinks are best-effort — the Firestore write is the
// primary record (see saveFunnelEvent for why GA4 alone is not enough), GA4 is a convenience
// for live monitoring.
import { track } from './analytics'
import { saveFunnelEvent } from './sessionStore'
import { LABS_BUILD } from './build'
import { CaptureEnv } from '../lib/env'

export type FunnelStep =
  | 'test-intro-shown' // the "Start the test" screen was put in front of the participant
  | 'test-click' // the button was pressed — logged BEFORE any work that could fail
  | 'test-started' // beginTest ran to completion and puzzle 0 was requested
  | 'test-puzzle-shown' // the first test puzzle actually RENDERED (see note below)
  | 'test-start-failed' // beginTest threw; the participant is stranded on a dead button
  | 'window-error' // an uncaught error / rejection anywhere in the labs app

// Why 'test-puzzle-shown' is separate from 'test-started':
// `test-started` only means beginTest RETURNED. It does not mean a puzzle reached the screen.
// Two ways it can lie:
//   1. startPuzzle(0) bails at its `if (overDailyCap())` guard, setting phase 'daily-limit'
//      and returning normally — no puzzle, no error, and test-started still logged;
//   2. the puzzle screen throws while React renders it, after the handler has returned.
// 'test-puzzle-shown' fires from an effect, i.e. only after a successful commit, so its
// ABSENCE distinguishes "the puzzle never appeared" from "they abandoned partway through it".
export type FunnelContext = {
  sessionId: string | null
  game: string
  revealArm?: string | null
  env?: CaptureEnv | null
  source?: { src: string | null; token: string | null }
}

export function logFunnel(step: FunnelStep, ctx: FunnelContext, detail?: Record<string, unknown>): void {
  try {
    saveFunnelEvent({ ...ctx, step, build: LABS_BUILD, detail }).catch(() => {
      /* best-effort: never let the instrument break the funnel */
    })
  } catch {
    /* ignore */
  }
  try {
    track('labs_funnel', {
      labs_step: step,
      labs_game: ctx.game,
      labs_build: LABS_BUILD,
      labs_reveal_arm: ctx.revealArm ?? 'none',
    })
  } catch {
    /* ignore */
  }
}

// Serialise a thrown value without assuming it is an Error. A string message and the first
// few stack frames are enough to tell "buildArmSession blew up" from "startPuzzle blew up".
export function errDetail(err: unknown): Record<string, unknown> {
  const e = err as { name?: string; message?: string; stack?: string }
  return {
    errName: e?.name ?? typeof err,
    errMessage: String(e?.message ?? err).slice(0, 300),
    errStack: (e?.stack ?? '').split('\n').slice(0, 6).join(' | ').slice(0, 600),
  }
}

// A try/catch around the click handler catches a synchronous throw inside it, but NOT an
// error thrown while React subsequently RENDERS the puzzle screen — which is an equally
// plausible way for the button to look dead. Window-level listeners catch both. Capped so a
// render loop cannot flood Firestore, and purely additive: nothing here changes what the
// participant sees.
const WINDOW_ERROR_CAP = 3
let windowErrorsLogged = 0
let installed = false

export function installWindowErrorLogger(getContext: () => FunnelContext): void {
  if (installed || typeof window === 'undefined') return
  installed = true
  const record = (detail: Record<string, unknown>) => {
    if (windowErrorsLogged >= WINDOW_ERROR_CAP) return
    windowErrorsLogged += 1
    logFunnel('window-error', getContext(), detail)
  }
  window.addEventListener('error', (e: ErrorEvent) => {
    record({ ...errDetail(e.error ?? e.message), src: e.filename ?? null, line: e.lineno ?? null })
  })
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    record({ ...errDetail(e.reason), kind: 'unhandledrejection' })
  })
}
